import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Container, Box, Typography, Card, Button, IconButton,
  Tabs, Tab, List, ListItem, ListItemText, Divider,
  TextField, InputAdornment, Tooltip, Badge, CircularProgress,
  Chip, Fade, ButtonGroup
} from '@mui/material'
import {
  Notifications as BellIcon,
  NotificationsNone as BellEmptyIcon,
  Delete as DeleteIcon,
  CheckCircleOutline as ReadIcon,
  DoneAll as CheckAllIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  TrendingUp as DealIcon,
  Alarm as AlarmIcon,
  Launch as LaunchIcon,
  ChevronRight as ChevronRightIcon,
  NotificationsOff as ClearAllIcon
} from '@mui/icons-material'
import { useNavigate } from 'react-router'
import { notificationsApi, AppNotification } from '../lib/api/notificationsApi'
import { themeColors } from '../app/layout/theme'
import { toast } from 'react-toastify'
import { ConfirmDialog } from '../components/shared/ConfirmDialog'

// ── Helpers ───────────────────────────────────────────────────────────────────

function relativeTime(iso: string) {
  const diff = Date.now() - new Date(iso).getTime()
  const mins  = Math.floor(diff / 60_000)
  const hours = Math.floor(diff / 3_600_000)
  const days  = Math.floor(diff / 86_400_000)
  if (mins < 1)    return 'just now'
  if (mins < 60)   return `${mins}m ago`
  if (hours < 24)  return `${hours}h ago`
  if (days === 1)  return 'yesterday'
  return `${days}d ago`
}

function entityRoute(n: AppNotification) {
  if (!n.entityId) return null
  if (n.entityType === 'DEAL')    return `/deals/${n.entityId}`
  if (n.entityType === 'LEAD')    return `/leads/${n.entityId}`
  if (n.entityType === 'CONTACT') return `/contacts/${n.entityId}`
  return null
}

function formatBody(body: string | null | undefined): string {
  if (!body) return ''
  return body.replace(/\[FID:[^\]]+\]/g, '').trim()
}

function getNotifColor(type: string) {
  if (type.startsWith('DEAL')) return themeColors.primary;
  if (type.includes('REMINDER') || type.includes('FOLLOW_UP')) return '#ef4444'; // Red
  if (type.includes('ASSIGNED')) return '#3b82f6'; // Blue
  return '#10b981'; // Green / default
}

function NotifIcon({ type }: { type: string }) {
  if (type.startsWith('DEAL')) {
    return <DealIcon sx={{ fontSize: 20, color: themeColors.primary }} />
  }
  if (type.includes('REMINDER') || type.includes('FOLLOW_UP')) {
    return <AlarmIcon sx={{ fontSize: 20, color: '#ef4444' }} />
  }
  return <BellIcon sx={{ fontSize: 20, color: '#3b82f6' }} />
}

export default function NotificationsPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [tabValue, setTabValue] = useState(0) // 0 = All, 1 = Unread
  const [searchQuery, setSearchQuery] = useState('')
  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false)

  // ── Fetch Notifications ─────────────────────────────────────────────────────
  const { data: notifications = [], isLoading, isRefetching, refetch } = useQuery({
    queryKey: ['notifications-page-list'],
    queryFn: () => notificationsApi.getAll(false, 100), // Get latest 100
  })

  // ── Mutations ───────────────────────────────────────────────────────────────
  const markReadMut = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] })
      qc.invalidateQueries({ queryKey: ['notifications-page-list'] })
      qc.invalidateQueries({ queryKey: ['notification-count'] })
      qc.invalidateQueries({ queryKey: ['unread-notifications-poll'] })
    },
    onError: () => toast.error('Failed to mark notification as read'),
  })

  const markAllReadMut = useMutation({
    mutationFn: notificationsApi.markAllRead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] })
      qc.invalidateQueries({ queryKey: ['notifications-page-list'] })
      qc.invalidateQueries({ queryKey: ['notification-count'] })
      qc.invalidateQueries({ queryKey: ['unread-notifications-poll'] })
      toast.success('All notifications marked as read')
    },
    onError: () => toast.error('Failed to mark all as read'),
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => notificationsApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] })
      qc.invalidateQueries({ queryKey: ['notifications-page-list'] })
      qc.invalidateQueries({ queryKey: ['notification-count'] })
      qc.invalidateQueries({ queryKey: ['unread-notifications-poll'] })
      toast.success('Notification deleted')
    },
    onError: () => toast.error('Failed to delete notification'),
  })

  const clearAllMut = useMutation({
    mutationFn: notificationsApi.clearAll,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] })
      qc.invalidateQueries({ queryKey: ['notifications-page-list'] })
      qc.invalidateQueries({ queryKey: ['notification-count'] })
      qc.invalidateQueries({ queryKey: ['unread-notifications-poll'] })
      toast.success('All notifications cleared successfully')
    },
    onError: () => toast.error('Failed to clear notifications'),
  })

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleNotificationClick = (n: AppNotification) => {
    if (!n.isRead) {
      markReadMut.mutate(n.id)
    }
    const route = entityRoute(n)
    if (route) {
      navigate(route)
    }
  }

  const handleMarkSingleRead = (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    markReadMut.mutate(id)
  }

  const handleDeleteSingle = (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    deleteMut.mutate(id)
  }

  const handleClearAll = () => {
    setIsClearConfirmOpen(true)
  }

  // ── Filters & Search ────────────────────────────────────────────────────────
  const filteredNotifications = useMemo(() => {
    return notifications
      .filter((n) => {
        // Tab filter (unread only)
        if (tabValue === 1 && n.isRead) return false
        
        // Search query filter
        if (searchQuery.trim()) {
          const query = searchQuery.toLowerCase()
          const matchesTitle = n.title.toLowerCase().includes(query)
          const matchesBody = n.body?.toLowerCase().includes(query) || false
          return matchesTitle || matchesBody
        }

        return true
      })
  }, [notifications, tabValue, searchQuery])

  const unreadCount = useMemo(() => {
    return notifications.filter(n => !n.isRead).length
  }, [notifications])

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: '12px',
            bgcolor: `${themeColors.primary}12`,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <BellIcon sx={{ color: themeColors.primary, fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={800} color="#1e293b" letterSpacing="-0.5px">
              Notification Center
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View and manage system alerts, reminders, and assignments.
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetch()} disabled={isLoading || isRefetching} sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0' }}>
              {isLoading || isRefetching ? <CircularProgress size={20} /> : <RefreshIcon sx={{ fontSize: 20 }} />}
            </IconButton>
          </Tooltip>
          
          <ButtonGroup size="small" variant="outlined" sx={{ bgcolor: '#fff' }}>
            <Button
              onClick={() => markAllReadMut.mutate()}
              disabled={unreadCount === 0 || markAllReadMut.isPending}
              startIcon={<CheckAllIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none', fontWeight: 600, borderColor: '#e2e8f0', color: 'text.primary', '&:hover': { borderColor: themeColors.primary } }}
            >
              Mark all read
            </Button>
            <Button
              onClick={handleClearAll}
              disabled={notifications.length === 0 || clearAllMut.isPending}
              startIcon={<ClearAllIcon sx={{ fontSize: 16 }} />}
              color="error"
              sx={{ textTransform: 'none', fontWeight: 600, borderColor: '#e2e8f0', '&:hover': { bgcolor: '#fef2f2' } }}
            >
              Clear all
            </Button>
          </ButtonGroup>
        </Box>
      </Box>

      {/* Main Container */}
      <Card sx={{
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
        border: '1px solid #ebebf5',
        overflow: 'hidden',
        bgcolor: '#ffffff'
      }}>
        {/* Navigation / Filter Bar */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          borderBottom: '1px solid #f1f5f9',
          bgcolor: '#fafafa'
        }}>
          <Tabs
            value={tabValue}
            onChange={(_, val) => setTabValue(val)}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '14px',
                minWidth: 90,
                py: 2,
                color: 'text.secondary',
              },
              '& .Mui-selected': {
                color: `${themeColors.primary} !important`
              },
              '& .MuiTabs-indicator': {
                backgroundColor: themeColors.primary,
                height: 3,
                borderTopLeftRadius: 3,
                borderTopRightRadius: 3
              }
            }}
          >
            <Tab label="All" />
            <Tab label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>Unread</span>
                {unreadCount > 0 && (
                  <Chip
                    label={unreadCount}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '10px',
                      fontWeight: 700,
                      bgcolor: themeColors.primary,
                      color: '#ffffff',
                      '& .MuiChip-label': { px: 0.8 }
                    }}
                  />
                )}
              </Box>
            } />
          </Tabs>

          {/* Inline Search */}
          <TextField
            placeholder="Search notification details..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.disabled', fontSize: 18 }} />
                </InputAdornment>
              ),
              sx: {
                bgcolor: '#ffffff',
                fontSize: '13px',
                borderRadius: '8px',
                width: 240,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.primary }
              }
            }}
          />
        </Box>

        {/* Notifications List */}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : filteredNotifications.length === 0 ? (
          <Fade in={true}>
            <Box sx={{ py: 10, px: 3, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box sx={{
                width: 80, height: 80, borderRadius: '50%',
                bgcolor: '#f8fafc',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                mb: 2,
                border: '1px dashed #e2e8f0'
              }}>
                <BellEmptyIcon sx={{ fontSize: 40, color: '#94a3b8' }} />
              </Box>
              <Typography variant="h6" fontWeight={700} color="#334155" mb={0.5}>
                {searchQuery ? 'No matching notifications' : 'All caught up!'}
              </Typography>
              <Typography variant="body2" color="text.secondary" maxWidth={360}>
                {searchQuery
                  ? 'Try adjusting your search query or switching tabs.'
                  : "You don't have any notifications right now. Enjoy your clean inbox!"}
              </Typography>
            </Box>
          </Fade>
        ) : (
          <List disablePadding>
            {filteredNotifications.map((n, i) => {
              const route = entityRoute(n)
              const notifColor = getNotifColor(n.type)

              return (
                <Fade in={true} key={n.id}>
                  <Box>
                    {i > 0 && <Divider sx={{ borderColor: '#f1f5f9' }} />}
                    <ListItem
                      onClick={() => handleNotificationClick(n)}
                      sx={{
                        px: 3.5, py: 2.2,
                        cursor: route ? 'pointer' : 'default',
                        bgcolor: n.isRead ? 'transparent' : `${themeColors.primary}04`,
                        transition: 'all 0.2s ease',
                        borderLeft: `3px solid ${n.isRead ? 'transparent' : notifColor}`,
                        '&:hover': {
                          bgcolor: '#f8fafc',
                          '& .action-buttons': { opacity: 1 }
                        },
                        alignItems: 'flex-start',
                        gap: 2.5,
                      }}
                    >
                      {/* Left Side: Avatar/Icon */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75, mt: '4px' }}>
                        <Box sx={{
                          width: 40, height: 40, borderRadius: '10px',
                          bgcolor: `${notifColor}10`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0,
                        }}>
                          <NotifIcon type={n.type} />
                        </Box>
                        {!n.isRead && (
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: notifColor }} />
                        )}
                      </Box>

                      {/* Content Area */}
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                            <Typography fontSize="14.5px" fontWeight={n.isRead ? 600 : 800} color={n.isRead ? '#334155' : '#0f172a'}>
                              {n.title}
                            </Typography>
                            {n.entityType && (
                              <Chip
                                label={n.entityType}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '9px',
                                  fontWeight: 700,
                                  bgcolor: `${notifColor}15`,
                                  color: notifColor,
                                  border: `1px solid ${notifColor}30`,
                                  '& .MuiChip-label': { px: 0.8 }
                                }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            {n.body && (
                              <Typography fontSize="13px" color="#64748b" sx={{ lineHeight: 1.45, mb: 0.75 }}>
                                {formatBody(n.body)}
                              </Typography>
                            )}
                            <Typography fontSize="11.5px" color="text.disabled" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {relativeTime(n.createdAt)}
                            </Typography>
                          </Box>
                        }
                        disableTypography
                      />

                      {/* Right Action Hover Area */}
                      <Box
                        className="action-buttons"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          opacity: 0,
                          transition: 'opacity 0.15s ease',
                          alignSelf: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {!n.isRead && (
                          <Tooltip title="Mark as read">
                            <IconButton
                              size="small"
                              onClick={(e) => handleMarkSingleRead(e, n.id)}
                              sx={{
                                color: 'text.secondary',
                                '&:hover': { color: themeColors.primary, bgcolor: `${themeColors.primary}10` }
                              }}
                            >
                              <ReadIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {route && (
                          <Tooltip title="View details">
                            <IconButton
                              size="small"
                              sx={{
                                color: 'text.secondary',
                                '&:hover': { color: themeColors.primary, bgcolor: `${themeColors.primary}10` }
                              }}
                            >
                              <LaunchIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={(e) => handleDeleteSingle(e, n.id)}
                            sx={{
                              color: 'text.secondary',
                              '&:hover': { color: '#ef4444', bgcolor: '#fef2f2' }
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItem>
                  </Box>
                </Fade>
              )
            })}
          </List>
        )}
      </Card>

      <ConfirmDialog
        open={isClearConfirmOpen}
        title="Clear All Notifications"
        description="Are you sure you want to permanently clear all notification history?"
        confirmLabel="Clear All"
        confirmColor="error"
        isConfirming={clearAllMut.isPending}
        onClose={() => setIsClearConfirmOpen(false)}
        onConfirm={() => {
          clearAllMut.mutate(undefined, {
            onSuccess: () => {
              setIsClearConfirmOpen(false)
            }
          })
        }}
      />
    </Container>
  )
}
